import { enhancedPedalMatch } from './pedalDatabase';

// ---------------------------------------------------------------------------
// Keyword exclusions
//
// A want may carry `excludedKeywords`: terms that veto a listing outright, so
// someone wanting "any delay under $100" never has to keep seeing the Behringer
// one. This is a hand-maintained mirror of the block in functions/matching.js —
// src/ cannot import from functions/, and only functions/ is uploaded on deploy.
// Edit both or the Wantlist Matches page (which recomputes in the browser) will
// disagree with the server-created matches. src/utils/__tests__ and
// functions/test run the same fixtures, and a parity test compares the two.
// ---------------------------------------------------------------------------

// How many keywords are honoured. The server predicate runs once per want per
// listing event, so an unbounded list is the one way this becomes expensive.
// firestore.rules caps writes at the same number; this clamp is what holds for
// documents written before that rule, or by the Admin SDK, which bypasses rules.
export const MAX_EXCLUDED_KEYWORDS = 20;

// Longest run of consecutive tokens one keyword may span. The length check
// inside the scan does the real bounding; this is a cheap backstop.
const MAX_KEYWORD_SPAN = 6;

// A hit preceded by one of these is ignored, so the keyword "modified" does not
// exclude a listing advertising itself as "never modified" — which is exactly
// backwards from what the user asked for. A heuristic, not a rule: it will not
// catch "came to me modified but I reverted it".
const EXCLUSION_NEGATORS = new Set(['no', 'not', 'never', 'non', 'without', 'zero']);

// Prose-safe tokenizer. Treating anything that is not a letter or number as a
// separator strips sentence punctuation (so "panel." matches "panel") while
// keeping accented brand names as single tokens, which \b would not.
const tokenizeForExclusion = (text) =>
  String(text == null ? '' : text)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);

// Keywords are compared with their separators deleted, so "DD-500", "DD 500"
// and "dd500" are one term regardless of which spelling the listing used.
export const squashForExclusion = (text) =>
  String(text == null ? '' : text).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

// True when the already-squashed `key` equals a run of consecutive tokens.
// Comparing against a whole run rather than a substring keeps "blue" from
// killing a Bluesbreaker; allowing a run rather than a single token lets
// "silver panel" match while leaving "silver ... panel" words apart alone.
const tokensContainKeyword = (tokens, key) => {
  if (!key) return false;
  for (let i = 0; i < tokens.length; i++) {
    let run = '';
    for (let k = 0; k < MAX_KEYWORD_SPAN && i + k < tokens.length; k++) {
      run += tokens[i + k];
      if (run.length > key.length) break;
      if (run !== key) continue;
      const preceding = tokens[i - 1];
      // Growing the window further can only overshoot, so a negated hit ends
      // this start position rather than continuing.
      if (preceding && EXCLUSION_NEGATORS.has(preceding)) break;
      return true;
    }
  }
  return false;
};

// Each field is tokenized on its own so a run cannot straddle two of them: a
// pedal with make "Silver" and a description opening "Panel mount jacks" must
// not read as "silver panel".
const buildExclusionFields = (pedal) =>
  [pedal.make, pedal.model, pedal.color, ...(pedal.effectTypes || []), pedal.description]
    .filter(Boolean)
    .map(tokenizeForExclusion);

// Squashing a want's keywords is per-want work happening inside per-listing
// loops, so it is memoised against the want object. A WeakMap rather than a
// property so we never mutate documents the caller may write back.
const squashedKeywordCache = new WeakMap();

const getSquashedKeywords = (want) => {
  const cached = squashedKeywordCache.get(want);
  if (cached) return cached;

  const keys = [];
  for (const entry of want.excludedKeywords.slice(0, MAX_EXCLUDED_KEYWORDS)) {
    const key = squashForExclusion(entry);
    if (key) keys.push(key);
  }
  squashedKeywordCache.set(want, keys);
  return keys;
};

export const isExcludedByKeywords = (want, pedal) => {
  if (!want || !pedal) return false;
  const raw = want.excludedKeywords;
  // Bail out before touching the listing: this is the common path, and it is
  // what keeps the cost proportional to the wants actually using the feature.
  if (!Array.isArray(raw) || raw.length === 0) return false;

  const keys = getSquashedKeywords(want);
  if (keys.length === 0) return false;

  const fields = buildExclusionFields(pedal);
  return keys.some(key => fields.some(tokens => tokensContainKeyword(tokens, key)));
};

/**
 * UNIFIED matching logic for Wantlist
 * This is the SINGLE SOURCE OF TRUTH - used by all components
 * 
 * Logic: ALL specified criteria must match (AND logic)
 * - If make is specified, pedal make must match
 * - If model is specified, pedal model must match
 * - If effect types are specified, pedal must have at least one matching effect
 * - Price, condition, and color are checked separately in doesPedalFullyMatchWant
 */
export const doesPedalMatchWant = (want, pedal) => {
  // Exclusions are a veto, so they run before anything else and apply to every
  // caller that reaches this predicate.
  if (isExcludedByKeywords(want, pedal)) return false;

  // Check make (if specified)
  if (want.make && want.make.trim() !== '') {
    if (!pedal.make) return false;
    // Use enhanced matching to handle aliases and variations (e.g., "EHX" = "Electro-Harmonix")
    const makeMatch = enhancedPedalMatch({ make: want.make, model: pedal.model || '' }, { make: pedal.make, model: pedal.model || '' });
    if (!makeMatch) return false;
  }
  
  // Check model (if specified)
  if (want.model && want.model.trim() !== '') {
    if (!pedal.model) return false;
    // Use enhanced matching for full make+model
    const fullMatch = enhancedPedalMatch(want, pedal);
    if (!fullMatch) return false;
  }
  
  // Check effect types (if specified)
  if (want.effectTypes && want.effectTypes.length > 0) {
    if (!pedal.effectTypes || pedal.effectTypes.length === 0) return false;
    // Pedal must have at least one matching effect type
    const hasMatchingEffect = want.effectTypes.some(wantType =>
      pedal.effectTypes.includes(wantType)
    );
    if (!hasMatchingEffect) return false;
  }
  
  // Must have at least ONE criterion specified
  const hasCriteria = (want.make && want.make.trim() !== '') ||
                      (want.model && want.model.trim() !== '') ||
                      (want.effectTypes && want.effectTypes.length > 0);
  
  if (!hasCriteria) return false;
  
  return true;
};

// Ordered ranking of conditions, best (highest) to worst (lowest). A want's
// `condition` is treated as the MINIMUM acceptable condition.
export const CONDITION_RANKING = {
  'Damaged': 1,
  'Poor': 2,
  'Good': 3,
  'Very Good': 4,
  'Excellent': 5,
  'New': 6
};

/**
 * Complete matching logic including price, condition, and color filters
 * This is used by the match checker and should be the full validation
 */
export const doesPedalFullyMatchWant = (want, pedal) => {
  // First check basic pedal matching
  if (!doesPedalMatchWant(want, pedal)) {
    return false;
  }

  // Check price (pedal price should be <= want max price)
  if (want.maxPrice && pedal.price > want.maxPrice) {
    return false;
  }

  // Check condition ranking (if specified)
  if (want.condition) {
    const pedalConditionRank = CONDITION_RANKING[pedal.condition] || 0;
    const wantConditionRank = CONDITION_RANKING[want.condition] || 0;

    if (pedalConditionRank < wantConditionRank) {
      return false;
    }
  }

  // Check decade (if specified)
  if (want.decade && want.decade.trim() !== '') {
    if (!pedal.decade || pedal.decade !== want.decade) {
      return false;
    }
  }

  // Check color (if specified) — lenient: if the pedal has no color set,
  // treat as compatible (we can't rule it out). Mirrors the server policy.
  if (want.color && want.color.trim() !== '') {
    if (pedal.color && pedal.color.trim() !== '') {
      if (pedal.color.toLowerCase() !== want.color.toLowerCase()) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Returns the specific want fields whose criteria currently EXCLUDE a given
 * listing, along with the listing value that would make them match. This is the
 * inverse of the price/condition/decade/color checks in doesPedalFullyMatchWant,
 * so it stays in lockstep with the matcher (and the "what's different" conflict
 * modal). Make/model/effect are intentionally excluded: this is only meaningful
 * for a want that already matches the listing on those basics.
 *
 * @returns {Object} e.g. { maxPrice: 360, condition: 'Very Good' } — only the
 *   fields that need updating, keyed by the want field name, valued with the
 *   listing's value (the suggested new criterion).
 */
export const getWantMismatchFields = (want, listing) => {
  const mismatches = {};
  if (!want || !listing) return mismatches;

  // Max price: want excludes the listing when the listing costs more than the cap.
  if (want.maxPrice && listing.price != null && listing.price !== '' &&
      Number(listing.price) > Number(want.maxPrice)) {
    mismatches.maxPrice = listing.price;
  }

  // Min condition: want excludes the listing when it ranks below the minimum.
  if (want.condition && listing.condition) {
    const listingRank = CONDITION_RANKING[listing.condition] || 0;
    const wantRank = CONDITION_RANKING[want.condition] || 0;
    if (listingRank < wantRank) {
      mismatches.condition = listing.condition;
    }
  }

  // Decade: want excludes the listing when it pins a different decade.
  if (want.decade && want.decade.trim() !== '' &&
      listing.decade && listing.decade !== want.decade) {
    mismatches.decade = listing.decade;
  }

  // Color: want excludes the listing when it pins a different (known) color.
  if (want.color && want.color.trim() !== '' &&
      listing.color && listing.color.trim() !== '' &&
      listing.color.toLowerCase() !== want.color.toLowerCase()) {
    mismatches.color = listing.color;
  }

  return mismatches;
};
